import { useNavigate } from "react-router-dom";
import { useContext, createContext, useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  OAuthProvider,
  onIdTokenChanged,
  SAMLAuthProvider,
  unlink
} from "firebase/auth";

import { auth } from "../configs/firebase.config";

import { showErrorToast } from "../utils/error.util";

const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleAuthError = (error) => {
    let errorMessage = "Something went wrong while login";

    const stringWithJson = error?.message;
    const startIndex = stringWithJson.indexOf("{");
    const endIndex = stringWithJson.lastIndexOf("}");

    if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex) {
      errorMessage = "Something went wrong while login";
    } else {
      const jsonString = stringWithJson.substring(startIndex, endIndex + 1);

      try {
        const jsonObject = JSON.parse(jsonString);
        errorMessage =
          jsonObject?.error?.message || "Something went wrong while login";
      } catch (error) {
        errorMessage = "Something went wrong while login";
      }
    }

    showErrorToast(errorMessage);
  }; 

  const googleSignIn = async () => {
    const provider = new GoogleAuthProvider();

    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      handleAuthError(error);
    }
  };

  const microsoftSignIn = async () => {
    const provider = new OAuthProvider("microsoft.com");

    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      handleAuthError(error);
    }
  };

  const oktaSignIn = async (email) => {
    // Dummy mapping for SAML providers based on email domain
    const domainToProvider = {
      // "company.com": "saml.okta",
      "company.b.com": "saml.okta-b",
      "reckitt.com": "saml.okta-rb"
    };

    // Extract domain from email
    const domain = email.split('@')[1];
    const providerId = domainToProvider[domain] || "";

    if (providerId === "") {
      showErrorToast("This email is not configured for use.");
      return;
    }

    const provider = new SAMLAuthProvider(providerId);
    
    // Pre-populate email in the sign-in popup if possible
    provider.setCustomParameters({
      login_hint: email
    });
    
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      handleAuthError(error);
    }
  };

  const logOut = async (shouldNavigate = true) => {
    try {
      await signOut(auth);
      if (shouldNavigate) {
        navigate("/login", { replace: true });
      }
    } catch (error) {
      let errorMessage = "Something went wrong while logout";
      showErrorToast(errorMessage);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    const unsubscribeToken = onIdTokenChanged(auth, (currentUser) => {
      setUser(currentUser);
    });


    return () => {
      unsubscribe();
      unsubscribeToken();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{ googleSignIn, microsoftSignIn, oktaSignIn, logOut, user, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
